import { createReducer, on } from '@ngrx/store';
import { AnnouncementEntitiesState } from './announcement-entities.state';
import { announcementEntitiesStateInitial } from './announcement-entities.state.initial';
import { AnnouncementEntitiesActions } from './announcement-entities.actions';
import { announcementEntitiesAdapter } from './announcement-entities.adapter';

/**
 * Reducer für Announcement-Entities.
 *
 * ARCHITEKTURENTSCHEIDUNG: upsertMany statt setAll bei loadListSuccess.
 *
 * Der Entity-Store ist ein geteilter Pool, aus dem mehrere Konsumenten
 * (Tabelle, Widgets) gleichzeitig lesen. Jeder Konsument steuert seine
 * Anzeige über eigene displayedIds auf Route-Level.
 *
 * setAll würde bei jedem Laden alle Entities ersetzen — wenn Tabelle und
 * Widget parallel laden, überschreiben sie sich gegenseitig.
 * upsertMany erweitert den Pool und aktualisiert bestehende Einträge.
 *
 * Trade-off: Gelöschte Entities bleiben als Stale Data im Store, wenn
 * sie von einer externen Quelle entfernt werden. Eigene Löschungen
 * werden über removeOne bei deleteSuccess sofort bereinigt.
 */
export const announcementEntitiesReducers = createReducer(
  announcementEntitiesStateInitial,

  on(
    AnnouncementEntitiesActions.loadList,
    (state): AnnouncementEntitiesState => ({
      ...state,
      isLoadingList: true,
      listError: null,
    }),
  ),

  on(
    AnnouncementEntitiesActions.loadListSuccess,
    (state, { announcements, totalElements }) =>
      announcementEntitiesAdapter.upsertMany(announcements, {
        ...state,
        totalElements,
        isLoadingList: false,
        isListLoaded: true,
        listError: null,
      }),
  ),

  on(
    AnnouncementEntitiesActions.loadListFailure,
    (state, { error }): AnnouncementEntitiesState => ({
      ...state,
      isLoadingList: false,
      isListLoaded: true,
      listError: error,
    }),
  ),

  on(
    AnnouncementEntitiesActions.loadStatistics,
    (state): AnnouncementEntitiesState => ({
      ...state,
      isLoadingStatistics: true,
      statisticsError: null,
    }),
  ),

  on(
    AnnouncementEntitiesActions.loadStatisticsSuccess,
    (state, { statistics }): AnnouncementEntitiesState => ({
      ...state,
      statistics,
      isLoadingStatistics: false,
      isStatisticsLoaded: true,
      statisticsError: null,
    }),
  ),

  on(
    AnnouncementEntitiesActions.loadStatisticsFailure,
    (state, { error }): AnnouncementEntitiesState => ({
      ...state,
      isLoadingStatistics: false,
      isStatisticsLoaded: true,
      statisticsError: error,
    }),
  ),

  on(
    AnnouncementEntitiesActions.delete,
    (state, { id }): AnnouncementEntitiesState => ({
      ...state,
      deletingIds: [...state.deletingIds, id],
    }),
  ),

  on(
    AnnouncementEntitiesActions.deleteSuccess,
    (state, { id }) =>
      announcementEntitiesAdapter.removeOne(id, {
        ...state,
        deletingIds: state.deletingIds.filter((i) => i !== id),
      }),
  ),

  on(
    AnnouncementEntitiesActions.deleteFailure,
    (state, { id }): AnnouncementEntitiesState => ({
      ...state,
      deletingIds: state.deletingIds.filter((i) => i !== id),
    }),
  ),

  on(
    AnnouncementEntitiesActions.publish,
    AnnouncementEntitiesActions.unpublish,
    (state, { id }): AnnouncementEntitiesState => ({
      ...state,
      publishingIds: [...state.publishingIds, id],
    }),
  ),

  on(
    AnnouncementEntitiesActions.publishSuccess,
    AnnouncementEntitiesActions.publishFailure,
    AnnouncementEntitiesActions.unpublishSuccess,
    AnnouncementEntitiesActions.unpublishFailure,
    (state, { id }): AnnouncementEntitiesState => ({
      ...state,
      publishingIds: state.publishingIds.filter((i) => i !== id),
    }),
  ),
);
